import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { action, runInAction, computed, observable, makeObservable } from "mobx";
import { API } from "General/API";
import { handleStoreState, CoreStore } from "Stores/RESTStore/CoreStore";
import { download } from "utils/download";
import { fileUploadStore } from "Pages/Project/Documents/FileUpload/FileUploadStore";
import {
  DocumentsFilters,
  DocumentsFetchParams,
  documentsSortByMap,
} from "Pages/Project/Documents/Stores/DocumentsTableStore";
import Axios, { CancelTokenSource } from "axios";

export class TemplatesStore extends CoreStore {
  data: API_FileDataEntity[] = [];
  isVisible = false;
  sortOrder: API_DOCUMENTSORTING_SORTORDER = "ASC";
  sortBy: keyof API_FileDataEntity = "name";
  filters: DocumentsFilters = {};
  fileTypeFilters: string[] = [];
  total: number = 0;
  defaultAmount = 30;
  uploadFolderId = -9;
  private cancelTokenSource: CancelTokenSource;

  constructor() {
    super();

    makeObservable(this, {
      data: observable,
      isVisible: observable,
      sortOrder: observable,
      sortBy: observable,
      filters: observable,
      fileTypeFilters: observable,
      total: observable,
      clear: action.bound,
      initModal: action.bound,
      hideModal: action.bound,
      fetchSortedBy: action.bound,
      fetchList: action.bound,
      fetchFiltered: action.bound,
      downloadFiles: action.bound,
      deleteFiles: action.bound,
      activeTemplates: computed,
      fetch: action,
      createWithTemplate: action.bound,
    });
  }

  url() {
    return API_URL.TEMPLATES.getAllTemplates_1(projectsDataStore.projectId);
  }

  cancelFetch = () => {
    this.cancelTokenSource.cancel();
  };

  clear() {
    this.data = [];
    this.filters = {};
    this.fileTypeFilters = [];
    this.sortOrder = "ASC";
    this.sortBy = "name";
    this.total = 0;
  }

  async initModal(opts: { folderId: number }): Promise<void> {
    this.isVisible = true;
    fileUploadStore.setFolderId(this.uploadFolderId);
    if (!this.isVisible) {
      return Promise.resolve(fileUploadStore.clear());
    } else {
      try {
        return await fileUploadStore.fetchFolderById(opts.folderId);
      } catch (error) {
        throw error;
      }
    }
  }

  hideModal() {
    this.isVisible = false;
    fileUploadStore.reset();
  }

  @handleStoreState()
  async fetch() {
    try {
      await this.fetchSortedBy({
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        amount: this.defaultAmount,
      });
    } catch (error) {
      throw error;
    }
  }

  async fetchSortedBy(params: {
    sortOrder: API_DOCUMENTSORTING_SORTORDER;
    sortBy: keyof API_FileDataEntity;
    amount?: number;
    offset?: number;
  }) {
    try {
      this.sortBy = params.sortBy;
      this.sortOrder = params.sortOrder;
      const response: API_MultiDocumentResponse = await this.fetchList({
        limit: params.amount || this.defaultAmount,
        offset: params.offset,
      });
      runInAction(() => {
        this.total = response.total;
        this.fileTypeFilters = response?.filter?.types || [];
        this.data = response.items.files;
      });
    } catch (error) {
      throw error;
    }
  }

  async fetchList(params: DocumentsFetchParams) {
    try {
      if (this.cancelTokenSource) {
        this.cancelFetch();
      }
      this.cancelTokenSource = Axios.CancelToken.source();
      params.sortBy = documentsSortByMap[this.sortBy];
      params.sortOrder = this.sortOrder;
      params.limit = params.limit || this.defaultAmount;
      params.showDeleted = params.showDeleted || false;
      if (this.filters.type) {
        params.type = this.filters.type.slice();
      }
      const response: API_MultiDocumentResponse = await API.getData(this.url(), params, {
        cancelToken: this.cancelTokenSource.token,
      });

      runInAction(() => {
        this.cancelTokenSource = null;
      });
      return response;
    } catch (err) {
      throw err;
    }
  }

  async fetchFiltered(filters: DocumentsFilters) {
    try {
      this.filters = { type: filters.type };
      await this.fetchSortedBy({
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        amount: this.defaultAmount,
      });
    } catch (error) {
      throw error;
    }
  }
  get getTotal() {
    return this.total;
  }

  async downloadFiles(fileIds: number[]) {
    try {
      let file: File;
      const files = this.getFilesFromIds(fileIds);
      if (files.some((item) => !item.fileActions || !item.fileActions.download)) {
        throw new Error("UNDOWNLOADABLE");
      }
      if (fileIds.length === 1) {
        file = await API.getBlob(API_URL.FILES.getFileById(projectsDataStore.projectId, fileIds[0]));
      } else {
        const formBody: API_MultipleFilesWithFoldersRequest = {
          files: files.map((item) => ({
            id: item.id,
            state: item.state,
          })),
          withFolders: false,
        };
        file = await API.getBlobByPost(API_URL.FILES.getFiles(projectsDataStore.projectId), formBody);
      }
      runInAction(() => {
        download(file, file.name);
        fileIds.forEach((id) => {
          const idx = this.data.findIndex((item) => item.id === id);
          this.data[idx].unread = false;
        });
      });
    } catch (error) {
      throw error;
    }
  }

  async deleteFiles(fileIds: number[]) {
    try {
      const formBody: API_MultipleFileRequest = this._formatDownloadFormBody(fileIds);
      await API.newData(API_URL.FILES.deleteFiles_1(projectsDataStore.projectId), formBody);
      this.fetch();
    } catch (error) {
      throw error;
    }
  }

  @handleStoreState()
  async createWithTemplate(formData: API_TemplateUploadRequest) {
    const body: API_TemplateUploadRequest = { ...formData, targetDir: fileUploadStore.folderId };
    try {
      const response: API_UploadData = await API.newData(
        API_URL.FILES.uploadFilesToTempDirectory(projectsDataStore.projectId),
        body
      );
      runInAction(() => {
        const { files } = response;
        if (files[0]) {
          const file = files[0];
          fileUploadStore.mergeExternalFiles(file);
        }
      });
    } catch (error) {
      throw error;
    }
  }

  _formatDownloadFormBody(fileIds: number[]): API_MultipleFileRequest {
    const files: API_MultipleFileRequest["files"] = fileIds.map((key) => {
      const foundFile = this.data.find((item) => item.id === +key) || {};
      if (foundFile.state === "SYMLINK") {
        return { id: foundFile.linkId, state: "SYMLINK" };
      } else {
        return { id: +key, state: foundFile.state };
      }
    });
    return { files };
  }

  get activeTemplates() {
    return this.data.filter((template) => template.state === "ACTIVE");
  }

  getFilesFromIds(fileIds: number[]) {
    const files: API_FileDataEntity[] = fileIds.reduce((arr, item) => {
      const foundItem = this.data.find((file) => file.id === item);
      if (foundItem) {
        arr.push(foundItem);
      } else {
        console.warn(`FILE ID "${item}" NOT FOUND`);
      }
      return arr;
    }, []);
    return files;
  }
}

export const templatesStore = new TemplatesStore();
